import { SITE_URL } from "./use-seo";

/**
 * JSON-LD structured-data builders for per-route SEO (#147).
 *
 * Ported from the legacy web/src/components/seo/JsonLd.tsx components —
 * reshaped from React <script> components into plain objects so the
 * useSeo hook can inject them into <head>. The site-wide LocalBusiness
 * node stays static in index.html and is not duplicated here.
 */

type JsonLd = Record<string, unknown>;

/** ItemList of the four sound-hire service types — used on /services. */
export const serviceJsonLd: JsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Sound Hire Services Wellington",
  description:
    "Professional sound equipment hire services in Wellington, New Zealand",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      item: {
        "@type": "Service",
        name: "Public Event Sound Hire Wellington",
        description:
          "Audio systems for ceremonies and receptions. Wireless microphones, weatherproof outdoor PA systems, aux input for playlists, and on-site technician.",
        provider: { "@type": "LocalBusiness", name: "Accent Productions" },
        areaServed: { "@type": "City", name: "Wellington" },
        serviceType: "Sound Equipment Hire",
      },
    },
    {
      "@type": "ListItem",
      position: 2,
      item: {
        "@type": "Service",
        name: "Corporate Event Audio Hire Wellington",
        description:
          "Professional audio for conferences, presentations, and company events. Gooseneck, handheld, and lapel microphones with distributed speaker coverage.",
        provider: { "@type": "LocalBusiness", name: "Accent Productions" },
        areaServed: { "@type": "City", name: "Wellington" },
        serviceType: "Corporate Audio Hire",
      },
    },
    {
      "@type": "ListItem",
      position: 3,
      item: {
        "@type": "Service",
        name: "Festival Sound System Hire Wellington",
        description:
          "Large-scale sound systems for outdoor events. Line arrays, stage monitors, and full front-of-house setups with experienced crew.",
        provider: { "@type": "LocalBusiness", name: "Accent Productions" },
        areaServed: { "@type": "City", name: "Wellington" },
        serviceType: "Festival Sound Production",
      },
    },
    {
      "@type": "ListItem",
      position: 4,
      item: {
        "@type": "Service",
        name: "Private Event Sound Hire Wellington",
        description:
          "Sound equipment for birthdays, anniversaries, and celebrations. Compact PA, CDJs, wireless mics with same-day delivery and collection.",
        provider: { "@type": "LocalBusiness", name: "Accent Productions" },
        areaServed: { "@type": "City", name: "Wellington" },
        serviceType: "Private Event Sound Hire",
      },
    },
  ],
};

/** The six Q&As surfaced as FAQ rich-result markup on /services. */
export const servicesFaq: ReadonlyArray<{ question: string; answer: string }> = [
  {
    question: "How much does sound hire cost in Wellington?",
    answer:
      "Pricing depends on your event size, venue, and requirements. A small private event with a basic PA system starts from around $500, while larger festival or corporate setups with full technician support range from $1,500 to $5,000+. Submit an inquiry and we'll send you a detailed quote within 24 hours.",
  },
  {
    question: "Do you deliver and set up the sound equipment?",
    answer:
      "Yes. Every full system hire includes delivery, professional setup, on-site operation by a qualified sound technician, and pack-down after your event. You don't need any technical knowledge. We handle everything.",
  },
  {
    question: "What size PA system do I need for my event?",
    answer:
      "For gatherings of 10-50 people (speeches, small parties), a compact powered speaker system works well. For 50-200 people (weddings, corporate events), you'll need a medium PA with subwoofers. For 200+ (festivals, outdoor events), we set up line arrays or multi-speaker systems with full monitoring. Tell us your event details and we'll recommend the right setup.",
  },
  {
    question: "How far in advance should I book sound hire?",
    answer:
      "We recommend booking at least 2-4 weeks ahead for smaller events and 1-3 months for larger events or peak season (November-March). Popular dates like New Year's Eve and Wellington Anniversary Weekend book out early.",
  },
  {
    question: "Do you provide a sound technician?",
    answer:
      "Yes. All full system hires include an experienced sound technician who manages your audio throughout the event. For equipment-only hires, you can add a technician at an hourly rate.",
  },
  {
    question: "What areas do you cover?",
    answer:
      "We're based in Wellington and cover the entire Wellington region including Lower Hutt, Upper Hutt, Porirua, and the Kapiti Coast. We also travel further afield for larger events. Just ask.",
  },
];

/** Builds an FAQPage node from question/answer pairs. */
export function faqJsonLd(
  faqs: ReadonlyArray<{ question: string; answer: string }>,
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}

/**
 * Builds a BreadcrumbList node. Each item's `path` is resolved against
 * SITE_URL so callers pass site-relative paths.
 */
export function breadcrumbJsonLd(
  items: ReadonlyArray<{ name: string; path: string }>,
): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: SITE_URL + item.path,
    })),
  };
}
